import {FunctionExecRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/FunctionExecRequest';
import {StopStreamRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/StopStreamRequest';
import {BooleanResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/BooleanResponse';
import {FunctionExecResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/FunctionExecResponse';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {
    ResponseConstructor,
    RestEngineSocketClient,
    WebSocketRequest,
    WebSocketResponse
} from '@mintdata-wsp/common/controllers/network/socket/RestEngineSocketClient';
import {SocketConfig} from '@mintdata-wsp/common/controllers/network/socket/SocketConfig';
import {FlowSimulation, MessageSource, SimulationId} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';

export class FlowCloudController
{
    private socketConfig:SocketConfig;
    private socketConfigPromise:Promise<SocketConfig>;
    private socketConfigResolve:(SocketConfig) => void;
    private socketConfigReject:(any) => void;

    private socketClient:RestEngineSocketClient;

    public init()
    {
        this._reInitSocketConfigPromise();
    }

    private _reInitSocketConfigPromise()
    {
        this.socketConfig = null;

        // it's safe to reject, even if it has already been resolved
        if (this.socketConfigReject)
            this.socketConfigReject("cancelled");

        this.socketConfigPromise = new Promise((resolve, reject) =>
        {
            this.socketConfigResolve = resolve;
            this.socketConfigReject = reject;
        });
    }

    private _resolveSocketConfigPromise()
    {
        Assert.yes(this.socketConfig, 'socketConfig should be set on that point');
        Assert.yes(this.socketConfigResolve, 'socketConfigResolve should be set on that point');
        this.socketConfigResolve(this.socketConfig);
    }

    public executeFunction(functionId:number, executionId:string, parameterValues:IFrame):Promise<FunctionExecResponse>
    {
        const request = new FunctionExecRequest(functionId, executionId, parameterValues, null);
        return this._ensureConnectedThenMakeRequest('exec-function', FunctionExecResponse, request);
    }

    public executeStream(functionId:number, executionId:string, parameterValues:IFrame):Promise<FunctionExecResponse>
    {
        const request = new FunctionExecRequest(functionId, executionId, parameterValues, null);
        return this._ensureConnectedThenMakeRequest('exec-stream', FunctionExecResponse, request);
    }

    public stopStream(functionId:number, executionId:string):Promise<BooleanResponse>
    {
        const request = new StopStreamRequest(executionId);
        return this._ensureConnectedThenMakeRequest('stop-stream', BooleanResponse, request);
    }

    private _ensureConnectedThenMakeRequest<T>(method:string,
                                               responseConstructor:ResponseConstructor,
                                               body:WebSocketRequest = null):Promise<WebSocketResponse>
    {
        return this.socketConfigPromise.then((socketConfig) =>
        {
            if( socketConfig !== this.socketConfig )
                return Promise.reject('Obsolete socket config detected - probably race-condition with designs opening/closing.');

            if( this.socketClient == null )
                this.socketClient = this._initializeSocketClient(socketConfig);

            return this.socketClient.request(method, responseConstructor, body);
        });
    }

    public setSocketConfig(socketConfig:SocketConfig)
    {
        Assert.yes(socketConfig, 'Socket config shoud not be null');

        this.disconnect();

        this.socketConfig = socketConfig;
        this._resolveSocketConfigPromise();
    }

    public disconnect()
    {
        if( this.socketConfig )
            this._reInitSocketConfigPromise();

        if( this.socketClient )
        {
            this.socketClient.disconnect();
            this.socketClient = null;
        }
    }

    private _initializeSocketClient(socketConfig:SocketConfig):RestEngineSocketClient
    {
        Assert.yes(socketConfig, 'socketConfig is not set');

        const socketClient = new RestEngineSocketClient(socketConfig);

        socketClient.subscribe('log', (data) =>
        {
            const {flowSimulationMessages, flowSimulation} = commonControllers;

            const logEvent = LogEventMessage.fromJSON(JSON.parse(data));

            const logMessage = logEvent.message ? logEvent.message.data : {};

            const simulationId = logEvent.topologyId;

            if( flowSimulation.shouldSkipCloudMessage(simulationId, logEvent) )
                return;

            const simulation = flowSimulation.getById(simulationId);
            Assert.yes(simulation);

            flowSimulationMessages.onMessageFromCloud(simulationId, logMessage);
        });

        socketClient.subscribe('control', (data) =>
        {
            const {flowSimulation, flowSimulationStatus, flowSimulationMessages} = commonControllers;

            const controlEvent = ControlEventMessage.fromJSON(JSON.parse(data));

            const simulationId = controlEvent.topologyId;

            if( flowSimulation.shouldSkipCloudMessage(simulationId, controlEvent) )
                return;

            const simulation = flowSimulation.getById(simulationId);
            Assert.yes(simulation);

            flowSimulationMessages.logMessage(simulationId, `Cloud flow is now ${controlEvent.state}`);
            flowSimulationStatus.onControlMessageFromCloud(simulationId, controlEvent);
        });

        socketClient.subscribe('data', (data) =>
        {
            const {flowSimulation, flowSimulationStatus, spreadsheets} = commonControllers;

            const dataEvent = DataEventMessage.fromJSON(JSON.parse(data));

            const simulationId = dataEvent.topologyId;
            const frame = dataEvent.message;

            if( flowSimulation.shouldSkipCloudMessage(simulationId, dataEvent) )
                return;

            const simulation = flowSimulation.getById(simulationId);
            Assert.yes(simulation);

            const outputComponentId = simulation.outputComponentId;
            const componentId = frame.getMeta().getValue('component_id');

            if( componentId === outputComponentId )
            {
                this.handleDataFrame(simulation, frame);
            }

            flowSimulation.markSawData(simulationId);

            flowSimulationStatus.onDataMessageFromCloud(simulationId, frame);
        });

        socketClient.addSocketListener({
            onError(event:Event):void
            {
                const {flowSimulation, flowSimulationStatus, flowSimulationMessages} = commonControllers;

                flowSimulation.getAll().forEach(({id}) =>
                {
                    flowSimulationMessages.logError(id, 'An error occurred on the live data channel', MessageSource.Network);
                    flowSimulationStatus.onErrorFromCloud(id, event);
                });
            }
        });

        return socketClient;
    }

    protected handleDataFrame(simulation:FlowSimulation, frame:IFrame)
    {
        const {spreadsheets} = commonControllers;

        if( simulation.isForSlot )
        {
            if( simulation.slot.isAccumulative )
                spreadsheets.sheet.addSlotValue(simulation.slot.sheet.id, simulation.slot.location, frame);
            else
                spreadsheets.sheet.setSlotValue(simulation.slot.sheet.id, simulation.slot.location, frame);
        }
    }
}

export interface IEventMessage
{
    topologyId:string;
}

export class LogEventMessage implements IEventMessage
{
    private constructor(public readonly topologyId:SimulationId,
                        public readonly message:{data:any})
    {
    }

    public static fromJSON(json:LogEventMessage)
    {
        return new LogEventMessage(json.topologyId, json.message);
    }
}

export class ControlEventMessage implements IEventMessage
{
    private constructor(public readonly topologyId:SimulationId,
                        public readonly state:TopologyStatus,
                        public readonly message:string,
                        public readonly timestamp:number)
    {
    }

    public static fromJSON(json:ControlEventMessage)
    {
        return new ControlEventMessage(json.topologyId, json.state, json.message, json.timestamp);
    }
}

export class DataEventMessage implements IEventMessage
{
    private constructor(public readonly topologyId:SimulationId,
                        public readonly message:IFrame)
    {
    }

    public static fromJSON(json:DataEventMessage)
    {
        const frame:IFrame = Frames.fromJSONObject(json.message as any);

        return new DataEventMessage(json.topologyId, frame);
    }
}

// Note: this enum *must* match mintdata.simulation.dispatch.TopologyState
export enum TopologyStatus
{
    /**
     * Request to allocate topology id arrived, but corresponding topology is not submitted
     */
    Allocated = 'allocated',

    /**
     * Topology id is allocated, and design for that topology id is provided
     */
    Submitted = 'submitted',

    /**
     * Topology is submitted, but has not posted any heartbeats.
     * Typical reason is resource starvation, when there are no available executors
     */
    Starving = 'starving',

    /**
     * Topology has posted heartbeat recently. Topology is running.
     */
    Running = 'running',

    /**
     * Topology was in running state but has not posted heartbeats recently.
     * May indicate transient connection errors.
     */
    Connection_Loss = 'connection_loss',

    /**
     * Topology was requested to stop, but has not terminated yet
     */
    Stopping = 'stopping',

    /**
     * Topology is terminated for whatever reason:
     * stopped by user request, unexpexted runtime exception, etc.
     */
    Terminated = 'terminated'
}
